import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useMyInfo } from "../../../@apis/hooks/useAuth";
import type { TournamentQrParams } from "../../../@apis/dto/request/tournament.request";
import { useLoginInfo } from "../../../hooks/useLoginInfo";
import { useModalStore } from "../../../lib/store/useModalStore";

// 실제 인증 코드는 대회별 QR 발급 API로 받아온다.
// 지금은 자리만 잡아두는 단계라 스캔되지 않는 표시용 값을 그린다.
const QR_PLACEHOLDER_VALUE = "PARKGOLF-QR-PLACEHOLDER";

/** 대회별로 묶여 내려오는 QR 목록을 슬라이드 단위(QR 하나)로 펼친다. */
type QrSlide = {
  key: string;
  tournamentId: number;
  qrId: number;
  /** 대회 유형에 따라 roundId 또는 calendarId 중 하나 */
  params: TournamentQrParams | null;
  tournamentName: string;
  qrLabel: string;
  isCheckedIn: boolean;
};

export default function HomeQrSwiper() {
  const { isLoggedIn } = useLoginInfo();
  const { openModal } = useModalStore();
  const { data: myInfoRes } = useQuery({
    ...useMyInfo(),
    enabled: isLoggedIn,
  });

  const slides: QrSlide[] = (myInfoRes?.data?.qrTournamentList ?? []).flatMap(
    (tournament) =>
      [...tournament.qrList]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((qr) => ({
          key: `${tournament.tournamentId}-${qr.qrId}`,
          tournamentId: tournament.tournamentId,
          qrId: qr.qrId,
          params:
            qr.roundId != null
              ? { roundId: qr.roundId }
              : tournament.calendarId != null
                ? { calendarId: tournament.calendarId }
                : null,
          tournamentName: tournament.tournamentName,
          qrLabel: qr.qrLabel,
          isCheckedIn: qr.isCheckedIn,
        })),
  );

  // 인증을 마친 QR은 더 볼 일이 없으므로 뒤로 밀어둔다. (sort는 안정 정렬이라 나머지 순서는 유지)
  slides.sort((a, b) => Number(a.isCheckedIn) - Number(b.isCheckedIn));

  // HomeQrSwiper가 팝업을 열 때 QR 목록 전체와, 누른 카드의 시작 인덱스를 넘깁니다.
  const dialogItems = slides.flatMap((slide) =>
    slide.params
      ? [
          {
            tournamentId: slide.tournamentId,
            params: slide.params,
            qrId: slide.qrId,
            tournamentName: slide.tournamentName,
            qrLabel: slide.qrLabel,
            isCheckedIn: slide.isCheckedIn,
          },
        ]
      : [],
  );

  const openQrCodeDialog = (slide: QrSlide) => {
    if (!slide.params || dialogItems.length === 0) return;

    const initialIndex = dialogItems.findIndex(
      (item) =>
        item.tournamentId === slide.tournamentId && item.qrId === slide.qrId,
    );

    openModal("TOURNAMENT_QR_CODE_DIALOG", {
      items: dialogItems,
      initialIndex: Math.max(0, initialIndex),
    });
  };

  // QR 일정이 있으면 홈 진입 시 팝업을 자동으로 띄운다.
  // 팝업을 닫으면 QR 목록을 다시 받아와 slides가 갱신되므로,
  // ref 가드가 없으면 닫자마자 다시 열리는 반복이 생긴다. (홈 진입당 1회만)
  const hasAutoOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    const target = slides.find((slide) => !slide.isCheckedIn && slide.params);
    if (!target) return;
    hasAutoOpenedRef.current = true;
    openQrCodeDialog(target);
  });

  if (slides.length === 0) return null;

  return (
    <section className="w-full pt-[15px]">
      <Swiper
        key={slides.length}
        modules={[Pagination]}
        slidesPerView={1}
        centeredSlides
        spaceBetween={10}
        observer
        observeParents
        updateOnWindowResize
        pagination={{ el: ".home-qr-pagination", clickable: true }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.key}>
            <div className="px-[15px]">
              <button
                type="button"
                disabled={slide.isCheckedIn || !slide.params}
                onClick={() => openQrCodeDialog(slide)}
                className="relative overflow-hidden w-full rounded-[10px] bg-white p-[20px] flex items-center gap-[15px]"
              >
                {/* 인증 완료: 다이얼로그 딤과 같은 검정 반투명 레이어를 카드 위에 덮는다 */}
                {slide.isCheckedIn && (
                  <div className="absolute inset-0 z-10 rounded-[10px] bg-black/40" />
                )}
                <div className="shrink-0">
                  <QRCodeSVG
                    value={QR_PLACEHOLDER_VALUE}
                    size={40}
                    level="M"
                    marginSize={0}
                  />
                </div>
                <div className="flex-1 min-w-0 flex flex-col items-start gap-[4px]">
                  <p className="w-full text-base font-semibold text-black break-keep text-left">
                    {slide.tournamentName}
                  </p>
                  <p className="w-full text-sm font-medium text-[#8e8e93] break-keep text-left">
                    {slide.isCheckedIn
                      ? `${slide.qrLabel} 인증 완료`
                      : slide.qrLabel}
                  </p>
                </div>
              </button>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {slides.length > 1 && (
        <div className="home-qr-pagination text-center w-full mt-[15px]" />
      )}
    </section>
  );
}
